import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BellMinus, BellOff, BellRing, Check, CheckCheck, Mail, MessageSquare, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import type { Alert, AlertSuppression, Resource } from '@itr/shared';
import { formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { AiAnalysisPanel } from '../components/AiAnalysis';
import { AiChatPanel } from '../components/AiChatPanel';
import { useAuth } from '../auth/AuthProvider';
import { useRealtimeEvent, useThrottledReload } from '../api/realtime';
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  bulkResultMessage,
  runBulk,
  useFetch,
  useSelection,
  useToast,
  type Selection,
  ReachDot,
} from '../components/ui';

const SEVERITY_INTENT = { critical: 'bad', warning: 'warn', info: 'info' } as const;
// `suppressed` is deliberately neutral rather than green: it is muted, not fixed.
const STATE_INTENT = { firing: 'bad', acknowledged: 'warn', suppressed: 'neutral', resolved: 'good' } as const;

type AlertListItem = Alert & {
  resourceName: string | null;
  siteName: string | null;
  customerName: string | null;
  suppressed: boolean;
  suppressionReason: string | null;
  suppressedUntil: number | null;
  suppressedBy: string | null;
};

/**
 * Suppression windows offered in the UI.
 *
 * `null` is the one people actually reach for — a known-broken box nobody is
 * fixing this quarter — but a timed mute is what you want for a maintenance
 * window, where forgetting to un-mute is the real failure mode.
 */
const SUPPRESS_DURATIONS: Array<[string, number | null]> = [
  ['1 hour', 3600],
  ['8 hours', 28_800],
  ['24 hours', 86_400],
  ['7 days', 604_800],
  ['30 days', 2_592_000],
  ['Until I lift it', null],
];

function suppressionNote(until: number | null): string {
  return until === null ? 'Suppressed indefinitely' : `Suppressed until ${new Date(until).toLocaleString()}`;
}

export function AlertsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const [state, setState] = useState<'open' | 'suppressed' | 'resolved' | ''>('open');
  const [severity, setSeverity] = useState('');

  const { data, loading, error, reload } = useFetch(
    () => api.get<AlertListItem[]>('/api/alerts', { state: state || undefined, severity: severity || undefined, limit: 200 }),
    [state, severity],
    { refreshMs: 30_000 },
  );

  // The list is capped, so the true size of the firing queue comes from the
  // summary — with more open alerts than fit, the ones still firing can sit
  // entirely below the cut.
  const summary = useFetch(
    () => api.get<{ firing: { critical: number; warning: number; info: number } }>('/api/alerts/summary'),
    [],
    { refreshMs: 30_000 },
  );
  const reloadAll = () => {
    reload();
    summary.reload();
  };

  // A new alert must appear without waiting for the next poll — that latency is
  // the difference between noticing an outage and hearing about it from a user.
  // Throttled, not delayed: the first frame reloads at once, a burst (an
  // acknowledge-all sends one frame per alert) collapses into one more.
  const liveReload = useThrottledReload(reloadAll, 2000);
  useRealtimeEvent(['alerts'], (event) => {
    if (event.type === 'alert.fired' || event.type === 'alert.resolved' || event.type === 'alert.updated') liveReload();
  });

  const alerts = data ?? [];
  const grouped = useMemo(() => {
    const firing = alerts.filter((a) => a.state === 'firing');
    const acknowledged = alerts.filter((a) => a.state === 'acknowledged');
    const resolved = alerts.filter((a) => a.state === 'resolved' || a.state === 'suppressed');
    return { firing, acknowledged, resolved };
  }, [alerts]);

  // Every firing alert under the current severity filter, loaded or not.
  const firing = summary.data?.firing;
  const firingCount =
    firing && (state === 'open' || state === '')
      ? severity
        ? firing[severity as keyof typeof firing] ?? 0
        : firing.critical + firing.warning + firing.info
      : grouped.firing.length;

  const ackAll = async () => {
    if (!confirm(`Acknowledge all ${firingCount} firing alerts?`)) return;
    try {
      const { acknowledged } = await api.post<{ acknowledged: number }>('/api/alerts/ack-all', { severity: severity || undefined });
      toast(`Acknowledged ${acknowledged} alerts`, 'good');
    } catch (err) {
      toast('Could not acknowledge all', 'bad', err instanceof Error ? err.message : undefined);
    }
    reloadAll();
  };

  const canAck = can('alert:ack');
  const canResolve = can('alert:write');
  // One drawer for the whole page, holding the ids it is about to silence —
  // a `position: fixed` drawer rendered inside a table row is a layout trap.
  // `fromSelection` keeps a single-row suppress from wiping a selection the
  // user is still building.
  const [suppressing, setSuppressing] = useState<{ ids: string[]; fromSelection: boolean } | null>(null);
  const selection = useSelection(alerts.map((a) => a.id));

  const bulkAck = async () => {
    const result = await runBulk(selection.selectedIds, (id) => api.post(`/api/alerts/${id}/ack`));
    const { message, intent } = bulkResultMessage('Acknowledged', result);
    toast(message, intent);
    selection.clear();
    reloadAll();
  };

  const bulkResolve = async () => {
    const result = await runBulk(selection.selectedIds, (id) => api.post(`/api/alerts/${id}/resolve`));
    const { message, intent } = bulkResultMessage('Resolved', result);
    toast(message, intent);
    selection.clear();
    reloadAll();
  };

  const applySuppression = async (opts: { reason: string; durationSec: number | null }) => {
    if (!suppressing) return;
    const result = await runBulk(suppressing.ids, (id) => api.post(`/api/alerts/${id}/suppress`, opts));
    const { message, intent } = bulkResultMessage('Suppressed', result);
    toast(message, intent);
    if (suppressing.fromSelection) selection.clear();
    setSuppressing(null);
    reloadAll();
  };

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Alerts</h1>
          <div className="page-subtitle">
            {firingCount} firing · {grouped.acknowledged.length} acknowledged
          </div>
        </div>
        <Link className="btn" to="/alerts/rules" title="The thresholds that raise alerts — CPU, memory, disk, health">
          <SlidersHorizontal size={15} /> Rules
        </Link>
        {can('alert:ack') && (firingCount > 1 || firingCount > grouped.firing.length) && (
          <button className="btn" onClick={ackAll}>
            <CheckCheck size={15} /> Acknowledge all
          </button>
        )}
      </div>

      <div className="row" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 4 }}>
        {(
          [
            ['open', 'Open'],
            ['suppressed', 'Suppressed'],
            ['resolved', 'Resolved'],
            ['', 'All'],
          ] as const
        ).map(([value, label]) => (
          <button key={label} className={`btn btn-sm${state === value ? ' btn-primary' : ''}`} onClick={() => setState(value)}>
            {label}
          </button>
        ))}
        <span style={{ width: 12 }} />
        {['critical', 'warning', 'info'].map((level) => (
          <button
            key={level}
            className={`btn btn-sm${severity === level ? ' btn-primary' : ''}`}
            onClick={() => setSeverity(severity === level ? '' : level)}
            style={{ textTransform: 'capitalize' }}
          >
            {level}
          </button>
        ))}
      </div>

      {(canAck || canResolve) && (
        <SelectionBar selection={selection}>
          {canAck && (
            <button className="btn btn-sm" onClick={bulkAck}>
              <Check size={12} /> Acknowledge
            </button>
          )}
          {canResolve && (
            <>
              <button className="btn btn-sm" onClick={bulkResolve}>
                <BellOff size={12} /> Resolve
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setSuppressing({ ids: selection.selectedIds, fromSelection: true })}
              >
                <BellMinus size={12} /> Suppress
              </button>
            </>
          )}
        </SelectionBar>
      )}

      <div className="card">
        {loading && !data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={6} cols={4} />
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : alerts.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck size={20} style={{ color: 'var(--itr-success)' }} />}
            title={state === 'open' ? 'Everything is healthy' : state === 'suppressed' ? 'Nothing suppressed' : 'No alerts'}
            text={
              state === 'open'
                ? 'No alerts are firing right now.'
                : state === 'suppressed'
                  ? 'Every condition is free to page someone.'
                  : 'Nothing matched these filters.'
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {(canAck || canResolve) && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={selection} />
                    </th>
                  )}
                  <th style={{ width: 90 }}>Severity</th>
                  <th>Alert</th>
                  <th style={{ width: 220 }}>Resource</th>
                  <th style={{ width: 110 }}>State</th>
                  <th data-align="right" style={{ width: 130 }}>
                    Last seen
                  </th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert) => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    onChanged={reloadAll}
                    onSuppress={() => setSuppressing({ ids: [alert.id], fromSelection: false })}
                    selection={canAck || canResolve ? selection : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {suppressing && (
        <SuppressDrawer count={suppressing.ids.length} onClose={() => setSuppressing(null)} onConfirm={applySuppression} />
      )}
    </>
  );
}

/**
 * Ask for the two things that decide whether a suppression is a good idea or a
 * future outage: how long it lasts, and why it exists.
 */
function SuppressDrawer({
  count,
  onClose,
  onConfirm,
}: {
  /** How many alerts this will silence; 1 from a row, more from the selection bar. */
  count: number;
  onClose: () => void;
  onConfirm: (opts: { reason: string; durationSec: number | null }) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('86400');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm({ reason: reason.trim(), durationSec: duration === 'forever' ? null : Number(duration) });
    } catch (err) {
      toast('Could not suppress', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={count > 1 ? `Suppress ${count} alerts` : 'Suppress this alert'}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            <BellMinus size={15} /> Suppress
          </button>
        </>
      }
    >
      <p className="subtle" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 0 }}>
        No further alerts on this issue. The condition keeps being checked and the occurrence count keeps rising, but
        nobody is paged — including when it clears and trips again.
      </p>

      <div className="field">
        <label className="field-label">For how long</label>
        <select className="select" value={duration} onChange={(e) => setDuration(e.target.value)}>
          {SUPPRESS_DURATIONS.map(([label, sec]) => (
            <option key={label} value={sec === null ? 'forever' : String(sec)}>
              {label}
            </option>
          ))}
        </select>
        <span className="field-help">
          A timed suppression lifts itself. An indefinite one stays until someone resumes alerting on it.
        </span>
      </div>

      <div className="field">
        <label className="field-label">Reason</label>
        <input
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Known issue, vendor RMA open, decommissioned…"
        />
        <span className="field-help">Shown to whoever wonders later why this stopped alerting.</span>
      </div>
    </Drawer>
  );
}

function AlertRow({
  alert,
  onChanged,
  onSuppress,
  selection,
}: {
  alert: AlertListItem;
  onChanged: () => void;
  /** Opens the page-level suppress drawer for this alert. */
  onSuppress: () => void;
  selection?: Selection;
}) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const ack = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.post(`/api/alerts/${alert.id}/ack`);
      toast('Acknowledged', 'good');
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const resume = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.post(`/api/alerts/${alert.id}/unsuppress`);
      toast('Alerting resumed', 'good');
      onChanged();
    } catch (err) {
      toast('Could not resume alerting', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const openTarget = () => {
    if (alert.resourceId) navigate(`/resources/${alert.resourceId}`);
    else navigate(`/alerts/${alert.id}`);
  };

  return (
    <tr data-clickable="true" onClick={openTarget}>
      {selection && (
        <td>
          <RowCheckbox selection={selection} id={alert.id} />
        </td>
      )}
      <td>
        <Badge intent={alert.state === 'resolved' ? 'good' : SEVERITY_INTENT[alert.severity]}>{alert.severity}</Badge>
      </td>
      <td>
        <div style={{ fontWeight: 550 }}>{alert.title}</div>
        {alert.description && (
          <div className="subtle truncate" style={{ fontSize: 12, maxWidth: 520, marginTop: 2 }}>
            {alert.description}
          </div>
        )}
        {alert.suppressed && (
          <div className="subtle truncate" style={{ fontSize: 11.5, maxWidth: 520, marginTop: 3 }}>
            <BellMinus size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
            {suppressionNote(alert.suppressedUntil)}
            {alert.suppressionReason ? ` — ${alert.suppressionReason}` : ''}
          </div>
        )}
      </td>
      <td style={{ fontSize: 12.5 }}>
        <div className="truncate" style={{ fontWeight: 550 }}>
          {alert.resourceName ?? alert.labels.kind ?? '—'}
        </div>
        {(alert.customerName || alert.siteName) && (
          <div className="subtle truncate" style={{ fontSize: 11.5, marginTop: 1 }}>
            {[alert.customerName, alert.siteName].filter(Boolean).join(' · ')}
          </div>
        )}
      </td>
      <td>
        <Badge intent={STATE_INTENT[alert.state]}>{alert.state}</Badge>
        {alert.occurrences > 1 && (
          <span className="subtle nums" style={{ fontSize: 11, marginLeft: 6 }}>
            ×{alert.occurrences}
          </span>
        )}
      </td>
      <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
        {formatRelative(alert.lastSeenAt)}
      </td>
      <td>
        <div className="row" style={{ gap: 2, justifyContent: 'flex-end' }}>
          {alert.state === 'firing' && can('alert:ack') && (
            <button className="btn btn-ghost btn-icon btn-sm" onClick={ack} disabled={busy} title="Acknowledge" aria-label="Acknowledge">
              <Check size={14} />
            </button>
          )}
          {can('alert:write') &&
            (alert.suppressed ? (
              <button
                className="btn btn-ghost btn-icon btn-sm"
                onClick={resume}
                disabled={busy}
                title="Resume alerting on this issue"
                aria-label="Resume alerting"
              >
                <BellRing size={14} />
              </button>
            ) : (
              alert.state !== 'resolved' && (
                <button
                  className="btn btn-ghost btn-icon btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSuppress();
                  }}
                  disabled={busy}
                  title="Suppress — no further alerts on this issue"
                  aria-label="Suppress"
                >
                  <BellMinus size={14} />
                </button>
              )
            ))}
        </div>
      </td>
    </tr>
  );
}

/* ================================================================== */
/* Detail                                                             */
/* ================================================================== */

interface AlertDetailData extends Alert {
  resource: Resource | null;
  /** The live suppression on this alert's condition, or null. */
  suppression: AlertSuppression | null;
  notifications: Array<{ channel: string; target: string; ok: number; error: string | null; created_at: number }>;
}

export function AlertDetail() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const toast = useToast();
  const [suppressOpen, setSuppressOpen] = useState(false);
  const { data, loading, error, reload } = useFetch(() => api.get<AlertDetailData>(`/api/alerts/${id}`), [id], {
    refreshMs: 20_000,
  });

  if (loading && !data) return <SkeletonTable rows={5} cols={3} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Alert not found" />;

  const act = async (action: 'ack' | 'resolve' | 'unsuppress') => {
    try {
      await api.post(`/api/alerts/${id}/${action}`);
      toast(action === 'ack' ? 'Acknowledged' : action === 'resolve' ? 'Resolved' : 'Alerting resumed', 'good');
      reload();
    } catch (err) {
      toast('Action failed', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div className="content-narrow">
      <div className="page-header">
        <div className="grow">
          <div className="row" style={{ gap: 10 }}>
            <Badge intent={data.state === 'resolved' ? 'good' : SEVERITY_INTENT[data.severity]}>{data.severity}</Badge>
            <Badge intent={STATE_INTENT[data.state]}>{data.state}</Badge>
          </div>
          <h1 className="page-title" style={{ marginTop: 8 }}>
            {data.title}
          </h1>
          {data.description && <div className="page-subtitle">{data.description}</div>}
        </div>
        <div className="row">
          {data.state === 'firing' && can('alert:ack') && (
            <button className="btn" onClick={() => act('ack')}>
              <Check size={15} /> Acknowledge
            </button>
          )}
          {/* Resuming stays available on a resolved alert: the suppression is on
              the condition, so it outlives this row and still needs lifting. */}
          {can('alert:write') &&
            (data.suppression ? (
              <button className="btn" onClick={() => act('unsuppress')}>
                <BellRing size={15} /> Resume alerting
              </button>
            ) : (
              data.state !== 'resolved' && (
                <button className="btn" onClick={() => setSuppressOpen(true)} title="No further alerts on this issue">
                  <BellMinus size={15} /> Suppress
                </button>
              )
            ))}
          {data.state !== 'resolved' && can('alert:write') && (
            <button className="btn btn-primary" onClick={() => act('resolve')}>
              <BellOff size={15} /> Resolve
            </button>
          )}
        </div>
      </div>

      {data.suppression && (
        <div className="card" style={{ marginBottom: 14, padding: '11px 16px' }}>
          <div className="row" style={{ gap: 10, fontSize: 12.5 }}>
            <BellMinus size={15} />
            <span className="grow">
              <strong style={{ fontWeight: 560 }}>{suppressionNote(data.suppression.expiresAt)}</strong>
              {data.suppression.reason ? ` — ${data.suppression.reason}` : ''}
              {data.suppression.createdByName ? (
                <span className="subtle"> · by {data.suppression.createdByName}</span>
              ) : null}
            </span>
          </div>
          <div className="subtle" style={{ fontSize: 11.5, marginTop: 4 }}>
            This condition is still evaluated and recorded, but it will not notify anyone — including if it resolves and
            fires again.
          </div>
        </div>
      )}

      <AiAnalysisPanel subjectType="alert" subjectId={data.id} />
      <AiChatPanel subjectType="alert" subjectId={data.id} />

      <div className="stat-strip">
        <div className="stat-chip">
          <div className="stat-chip-label">First seen</div>
          <div className="stat-chip-value" style={{ fontSize: 15, fontWeight: 560 }}>
            {formatRelative(data.firstSeenAt)}
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Occurrences</div>
          <div className="stat-chip-value">{data.occurrences}</div>
        </div>
        {data.value !== null && (
          <div className="stat-chip">
            <div className="stat-chip-label">Measured</div>
            <div className="stat-chip-value">
              {data.value}
              {data.threshold !== null && <span className="subtle" style={{ fontSize: 13 }}> / {data.threshold}</span>}
            </div>
          </div>
        )}
      </div>

      {data.resource && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <span className="card-title">Affected resource</span>
          </div>
          <Link
            to={`/resources/${data.resource.id}`}
            className="row"
            style={{ padding: '12px 16px', gap: 10, color: 'inherit', textDecoration: 'none' }}
          >
            <span className="grow" style={{ fontWeight: 550 }}>
              <ReachDot health={data.resource.health} />
              {data.resource.name}
            </span>
            <Badge>{data.resource.kind}</Badge>
          </Link>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-title">Notification history</span>
          <span className="card-subtitle" style={{ marginLeft: 'auto' }}>
            {data.notifications.length} sent
          </span>
        </div>
        {data.notifications.length === 0 ? (
          <EmptyState title="No notifications sent" text="This alert was recorded in-app only." />
        ) : (
          <div style={{ padding: '4px 0' }}>
            {data.notifications.map((n, i) => (
              <div key={i} className="row" style={{ padding: '9px 16px', gap: 10, fontSize: 12.5 }}>
                {n.channel === 'email' ? <Mail size={14} /> : <MessageSquare size={14} />}
                <span style={{ minWidth: 70, textTransform: 'capitalize' }}>{n.channel}</span>
                <span className="grow truncate mono">{n.target}</span>
                <Badge intent={n.ok ? 'good' : 'bad'}>{n.ok ? 'delivered' : 'failed'}</Badge>
                <span className="subtle" style={{ minWidth: 90, textAlign: 'right' }}>
                  {formatRelative(n.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {suppressOpen && (
        <SuppressDrawer
          count={1}
          onClose={() => setSuppressOpen(false)}
          onConfirm={async (opts) => {
            await api.post(`/api/alerts/${id}/suppress`, opts);
            toast('Suppressed', 'good');
            setSuppressOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}
